import Cookies from "js-cookie";
import GoogleMapReact from "google-map-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import { useCustomerAnalytics } from "../../../../services/hooks/useCustomerAnalytics";
import { useModalHook } from "../../../../services/hooks/useModal";
import { useScrollLock } from "../../../../services/hooks/useScrollLock";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../services/hooks/useReduxHooks";
import {
  setAnimatedGlobalBannerState,
  setBottomSheetState,
} from "../../../../services/redux/slices/globalComponentsToggleSlice";
import {
  setSelectedFranchise,
  sortActiveFranchisesByDistance,
} from "../../../../services/redux/slices/configSlice";
import { setCurrentDeliveryConfig } from "../../../../services/redux/slices/deliverySlice";
import {
  setClickDetected,
  setLocation,
} from "../../../../services/redux/slices/locationSlice";
import { setQueryParam } from "../../../../services/redux/slices/urlParamsSlice";
import { cn } from "../../../../utils/cn";
import {
  COOKIE_ENUM,
  CUSTOMER_ANALYTICS_EVENT,
  DeliveryTypeDisplayName,
  OrderTypesEnum,
  PagesEnum,
  URL_PARAMS,
} from "../../../../utils/enums";
import envConfig from "../../../../utils/env.config";
import { selectCartItemsNumber } from "../../../../services/redux/selectors/cartSelectors";
import { getPageLink } from "../../../../utils/help/navigation/navigation";
import { isOrderTypeSupported } from "../../../../utils/help/orderType/orderType";
import { Franchise } from "../../../../utils/types";
import MapLibreMapComponent from "../../../global/DeliveryMap/MapLibreMap";
import { BackArrowIcon } from "../../../global/Icons/Icons";
import { MapDetectLocationIcon, PinDeliveryIcon } from "../Map.icons";
import { MapScreenV3BranchList } from "../MapScreenV3/MapScreenV3BranchList";
import { SearchBoxV2 } from "../SearchBox/SearchBoxV2/SearchBoxV2";
import { useMapScreen } from "../useMapScreen";
import v3Styles from "../MapScreenV3/MapScreenV3.module.scss";
import styles from "./MapScreenV2.module.scss";
import { selectCurrentDeliveryConfig } from "../../../../services/redux/selectors/deliverySelectors";
import { findDeliveryConfigForBranch } from "../../../../utils/help/delivery/delivery";

type OrderTypeSelection =
  | OrderTypesEnum
  | "car_pickup"
  | `delivery_type_${number}`;

export const MapScreenV2 = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const addCustomerAnalytics = useCustomerAnalytics();
  const { handleModal } = useModalHook();

  const [searchInputContainer, setSearchInputContainer] =
    useState<HTMLDivElement | null>(null);
  const [hasClickedDetectLocation, setHasClickedDetectLocation] =
    useState(false);

  // ── V3 state ──────────────────────────────────────────
  const hasWelcomeDrawerV3 = useAppSelector(
    (state) => state.config.restaurantConfig?.hasWelcomeDrawerV3
  );
  // V2 gates its detect-location control on this same flag (LocationDropdown),
  // so the V3 branch-list FAB must honour it too — otherwise restaurants that
  // opted out of nearest-branch detection still get the button.
  const detectNearestBranchLocation = useAppSelector(
    (state) => state.config.restaurantConfig?.detectNearestBranchLocation
  );

  // Pin the page while the V3 location screen is mounted. The header, order-type
  // tabs and footer are sized against the viewport, so letting the document
  // scroll (its 100vh fallback overshoots the visible area on mobile Safari)
  // slid them out of view — Sarah's scroll glitch. The branch list / map panes
  // keep their own internal scroll.
  useScrollLock(Boolean(hasWelcomeDrawerV3));
  const orderTypeParamState = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );
  const activeFranchises = useAppSelector(
    (state) => state.config.activeFranchises
  );
  const reduxSelectedFranchise = useAppSelector(
    (state) => state.config.selectedFranchise
  );
  // selectCartItemsNumber, not the raw cartLoyaltyV2 field: that field stays 0
  // when `useLoyaltyService` is on, so the branch-switch confirmation below was
  // skipped entirely on loyalty-service restaurants (prepits/project#3913).
  const cartItemsNumber = useAppSelector(selectCartItemsNumber);

  const rawOrderType = router.query.order_type as string | undefined;
  const currentDeliveryConfig = useAppSelector(selectCurrentDeliveryConfig);

  // When URL has order_type=deliveryv2, resolve to the correct delivery tab.
  // Prefer currentDeliveryConfig (set by welcome drawer) so the user lands on
  // the same type they selected. Fall back to the first type in the list.
  const resolveDeliveryV2Tab = (): OrderTypeSelection => {
    if (currentDeliveryConfig?.deliveryType?.id) {
      const { id, displayName } = currentDeliveryConfig.deliveryType;

      return displayName === DeliveryTypeDisplayName.CAR_PICKUP
        ? "car_pickup"
        : (`delivery_type_${id}` as `delivery_type_${number}`);
    }

    const allDeliveryConfigs = activeFranchises.flatMap(
      (f) => f.franchiseConfig?.deliveryV2 ?? []
    );
    if (allDeliveryConfigs.length > 0) {
      const { id, displayName } = allDeliveryConfigs[0].deliveryType;

      return displayName === DeliveryTypeDisplayName.CAR_PICKUP
        ? "car_pickup"
        : (`delivery_type_${id}` as `delivery_type_${number}`);
    }

    return OrderTypesEnum.TAKE_OUT;
  };

  const initialTab: OrderTypeSelection =
    rawOrderType === "car_pickup"
      ? "car_pickup"
      : rawOrderType?.startsWith("delivery_type_")
      ? (rawOrderType as `delivery_type_${number}`)
      : rawOrderType === OrderTypesEnum.DELIVERYV2
      ? resolveDeliveryV2Tab()
      : (rawOrderType as OrderTypesEnum) || OrderTypesEnum.TAKE_OUT;

  const [activeTab, setActiveTab] = useState<OrderTypeSelection>(initialTab);
  const tabButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  // Guards the initial-tab sync below so a manual tab switch is never
  // overridden once the URL/config finish hydrating.
  const hasSyncedInitialTab = useRef(false);

  useEffect(() => {
    tabButtonRefs.current[activeTab]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [activeTab]);

  // order_type (and currentDeliveryConfig) can be absent on the first render
  // when the router hasn't hydrated, so useState(initialTab) may have captured
  // the TAKE_OUT fallback. Re-sync the tab once they're available instead of
  // sticking on the wrong one (review: initial tab misread before hydration).
  useEffect(() => {
    if (!router.isReady || hasSyncedInitialTab.current || !rawOrderType) {
      return;
    }
    setActiveTab(initialTab);
    hasSyncedInitialTab.current = true;
  }, [router.isReady, rawOrderType, currentDeliveryConfig?.deliveryType?.id]); // eslint-disable-line

  // Pre-select the already-confirmed franchise (if any) so returning to this
  // screen shows the prior choice highlighted instead of nothing selected.
  const [selectedFranchiseId, setSelectedFranchiseId] = useState<
    number | undefined
  >(() => reduxSelectedFranchise?.id);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDetecting, setIsDetecting] = useState(false);

  // ── Delivery map state (from useMapScreen) ────────────
  const {
    detailsSection,
    setDetailsSection,
    error,
    setError,
    setGoogleApiLoaded,
    setMapLibreLoaded,
    center,
    setCenter,
    zoom,
    setZoom,
    isMapLoaded,
    egyptDefaultProps,
    createMapOptions,
    enableInternalMaps,
    enableNewDeliveryExperience,
    franchiseConfig,
    onChange,
    onMapLibreChange,
    handleBack: v2HandleBack,
    handleSkip: v2HandleSkip,
    getCurrentPosition,
    headerBackTranslation,
    newAddressTranslation,
    addLocationTranslation,
    skipTranslation,
    t,
  } = useMapScreen();

  type TabEntry = {
    type: OrderTypeSelection;
    label: string;
    testId: string;
    id?: number;
  };

  // Derive tabs dynamically from franchises — mirrors SelectOrderInfoV3 logic
  const TAB_CONFIG = useMemo((): TabEntry[] => {
    const uniqueDeliveryTypes = Object.values(
      activeFranchises
        .flatMap((f) => f.franchiseConfig?.deliveryV2 ?? [])
        .reduce<Record<number, { id: number; displayName: string }>>(
          (acc, config) => {
            const { id, displayName } = config.deliveryType;
            if (!acc[id]) {
              acc[id] = { id, displayName };
            }

            return acc;
          },
          {}
        )
    );
    const hasTakeOut = activeFranchises.some((f) => f.franchiseConfig?.takeOut);
    const hasDineIn = activeFranchises.some((f) => f.franchiseConfig?.dineIn);
    const hasLegacyDelivery =
      uniqueDeliveryTypes.length === 0 &&
      activeFranchises.some((f) => f.franchiseConfig?.delivery);

    return [
      ...uniqueDeliveryTypes.map(({ id, displayName }) => ({
        type: (displayName === DeliveryTypeDisplayName.CAR_PICKUP
          ? "car_pickup"
          : `delivery_type_${id}`) as OrderTypeSelection,
        label: t(`table-page:${displayName.toLowerCase()}`),
        id,
        // displayName-based (stable across environments); DB ids differ per env.
        testId: `v3-tab-${displayName}`,
      })),
      ...(hasLegacyDelivery
        ? [
            {
              type: OrderTypesEnum.DELIVERYV2 as OrderTypeSelection,
              label: t("table-page:table-delivery"),
              testId: "v3-tab-delivery",
            },
          ]
        : []),
      ...(hasTakeOut
        ? [
            {
              type: OrderTypesEnum.TAKE_OUT as OrderTypeSelection,
              label: t("table-page:table-take-out"),
              testId: "v3-tab-pickup",
            },
          ]
        : []),
      ...(hasDineIn
        ? [
            {
              type: OrderTypesEnum.DINE_IN as OrderTypeSelection,
              label: t("table-page:table-dine-in"),
              testId: "v3-tab-dine-in",
            },
          ]
        : []),
    ];
  }, [activeFranchises, t]);

  const activeDeliveryTypeId: number | undefined = TAB_CONFIG.find(
    (t) => t.type === activeTab
  )?.id;

  // The selected branch's own row for the active tab's type. useSearchBox
  // commits this row on a fresh pin, so it must not be another branch's just
  // because that branch is listed first -- see findDeliveryConfigForBranch.
  const activeDeliveryConfig =
    activeDeliveryTypeId !== undefined
      ? findDeliveryConfigForBranch(
          reduxSelectedFranchise,
          activeFranchises,
          (c) => c.deliveryType.id === activeDeliveryTypeId
        ) ?? null
      : null;

  // Only table-delivery renders the map/address flow — every other delivery
  // type (car pickup, beach delivery, toktok pickup, ...) renders the branch
  // list like takeout/dine-in. Checked by name, not usesMap — some non-table
  // delivery types are still flagged usesMap:true in config data.
  const isDeliveryTab =
    activeTab === OrderTypesEnum.DELIVERY ||
    (activeTab === OrderTypesEnum.DELIVERYV2 &&
      activeDeliveryTypeId === undefined) ||
    activeDeliveryConfig?.deliveryType.displayName ===
      DeliveryTypeDisplayName.TABLE_DELIVERY;

  // Selecting a branch highlights only the icon for the order type currently
  // being booked — the card's other order-type icons stay grey. Keys match
  // getFranchiseOrderTypeIcons in MapScreenV3BranchList.
  const activeOrderIconKey: string | undefined =
    activeDeliveryConfig?.deliveryType.displayName ===
    DeliveryTypeDisplayName.CAR_PICKUP
      ? "car_pickup"
      : activeTab === OrderTypesEnum.TAKE_OUT
      ? "takeout"
      : activeTab === OrderTypesEnum.DINE_IN
      ? "dine_in"
      : undefined;

  // V3 delivery tab: when no saved location exists, seed center with restaurant
  // default so MapLibre can initialize and fire onLoaded (null lat/lng causes
  // silent failure). Runs on tab switch too, not just mount — starting on a
  // non-delivery tab leaves center unseeded, so switching to the map tab with a
  // null center failed silently (review: center not reseeded on tab switch).
  // The !center.lat guard keeps any already-set center intact.
  useEffect(() => {
    if (hasWelcomeDrawerV3 && isDeliveryTab && !center.lat) {
      setCenter(egyptDefaultProps.center);
    }
  }, [hasWelcomeDrawerV3, isDeliveryTab]); // eslint-disable-line

  // Clear map-loading spinner when V3 non-delivery branch list is shown
  useEffect(() => {
    if (hasWelcomeDrawerV3 && !isDeliveryTab) {
      setGoogleApiLoaded(true);
      setMapLibreLoaded(true);
    }
  }, [hasWelcomeDrawerV3, isDeliveryTab]);

  useEffect(() => {
    if (error && error.text && !error.success) {
      dispatch(
        setAnimatedGlobalBannerState({
          isOpen: true,
          text: error.text,
          warning: true,
        })
      );
    }
  }, [error]);

  // ── V3 handlers ───────────────────────────────────────
  // Whether a franchise supports a given branch-list tab (car pickup / any
  // non-map delivery type / takeout / dine-in). Map-based delivery tabs use
  // the map/address flow instead, not franchise selection.
  const isFranchiseSupportedByTab = (
    franchise: Franchise,
    tab: OrderTypeSelection
  ): boolean => {
    if (tab === "car_pickup") {
      return (franchise.franchiseConfig?.deliveryV2 ?? []).some(
        (c) => c.deliveryType.displayName === DeliveryTypeDisplayName.CAR_PICKUP
      );
    }
    if ((tab as string).startsWith("delivery_type_")) {
      const id = Number((tab as string).replace("delivery_type_", ""));

      return (franchise.franchiseConfig?.deliveryV2 ?? []).some(
        (c) => c.deliveryType.id === id
      );
    }

    return Boolean(
      isOrderTypeSupported(franchise.franchiseConfig, tab as OrderTypesEnum)
    );
  };

  const handleTabChange = (tab: OrderTypeSelection) => {
    if (tab === activeTab) {
      return;
    }

    // A manual switch is the user's intent — stop the hydration sync from
    // later snapping the tab back to the URL's order type.
    hasSyncedInitialTab.current = true;

    // Deliberately don't clear selectedFranchiseId here — filteredFranchises
    // (scoped to the active tab) already hides/shows the selection correctly,
    // so switching away and back to a tab restores the prior pick automatically.
    setSearchQuery("");
    setActiveTab(tab);
  };

  // Branch-name search on the location screen — report once per search session
  // (empty -> non-empty), not on every keystroke.
  const handleBranchSearchChange = (value: string) => {
    if (!searchQuery.trim() && value.trim()) {
      addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.SEARCHED_FOR_BRANCH);
    }
    setSearchQuery(value);
  };

  const v3HandleBack = () => {
    // Opened via "Add new address" inside the delivery drawer on the cart/tip
    // screen (handleAddNewLocation sets next_page). Back is a cancel, so return
    // the customer to that page instead of Home — otherwise they're dropped out
    // of checkout (prepits/project#3913). No delivery address was confirmed, so
    // restore the order type they had BEFORE the flow. redux orderTypeParam
    // can't be trusted here — the location screen (useMapScreen) synced the
    // URL's deliveryv2 into it — so use prev_order_type stamped on the way in,
    // falling back to redux.
    const returnPage = router.query[URL_PARAMS.NEXT_PAGE] as
      | PagesEnum
      | undefined;
    if (returnPage) {
      const restoredOrderType =
        (router.query[URL_PARAMS.PREV_ORDER_TYPE] as OrderTypesEnum) ||
        orderTypeParamState;
      const isDeliveryOrderType =
        restoredOrderType === OrderTypesEnum.DELIVERYV2 ||
        restoredOrderType === OrderTypesEnum.DELIVERY;

      // handleAddNewLocation overwrote currentDeliveryConfig with the address
      // tab's config. Restore what the customer actually had:
      // - non-delivery (pickup/dine-in) → clear it.
      // - deliveryV2 (car pickup, cabin, ...) → the coarse order type is only
      //   "deliveryv2", so re-resolve the specific config from the stamped
      //   prev_delivery_type_id; otherwise the cart would fall back to a
      //   generic/stale delivery type (prepits/project#3913).
      const prevDeliveryTypeId = router.query[URL_PARAMS.PREV_DELIVERY_TYPE_ID];
      if (!isDeliveryOrderType) {
        dispatch(setCurrentDeliveryConfig(null));
      } else if (prevDeliveryTypeId !== undefined) {
        const restoredConfig =
          reduxSelectedFranchise?.franchiseConfig?.deliveryV2?.find(
            (c) => c.deliveryType.id === Number(prevDeliveryTypeId)
          ) ?? null;
        dispatch(setCurrentDeliveryConfig(restoredConfig));
      }
      // Re-sync redux (useMapScreen left it on deliveryv2) — the cart banner
      // reads orderTypeParam from redux, not the URL.
      if (restoredOrderType) {
        dispatch(setQueryParam(restoredOrderType));
      }
      const link = getPageLink(router, returnPage, {
        franchiseSlug: reduxSelectedFranchise?.slug,
        orderType: restoredOrderType,
      });
      router.push(link.href, link.as);

      return;
    }

    if (hasWelcomeDrawerV3 && orderTypeParamState) {
      // User had an existing selection — go back preserving query params
      const link = getPageLink(router, PagesEnum.Home);
      router.push(link.href, link.as);
    } else {
      // User came from the welcome drawer — reopen it
      dispatch(setBottomSheetState(true));
      dispatch(setCurrentDeliveryConfig(null));
      const link = getPageLink(router, PagesEnum.Home, {
        resetQueryParams: true,
      });
      router.push(link.href, link.as);
    }
  };

  const v3HandleSkip = () => {
    // Explicit skip: mark the welcome flow skipped so useFranchiseData doesn't
    // re-open the welcome drawer on the home mount below (a missing franchise
    // cookie would otherwise force the customer to skip a second time). Session
    // cookie — clears when the browser closes, so a fresh visit still prompts.
    Cookies.set(COOKIE_ENUM.WELCOME_DRAWER_SKIPPED, "true");
    router.push(getPageLink(router, PagesEnum.Home, {}).href);
  };

  const filteredFranchises = useMemo(() => {
    if (isDeliveryTab) {
      return [];
    }

    const byOrderType = activeFranchises.filter((f) =>
      isFranchiseSupportedByTab(f, activeTab)
    );

    if (!searchQuery.trim()) {
      return byOrderType;
    }

    const q = searchQuery.toLowerCase();

    // Match the localized name too, so searching in Arabic finds branches whose
    // display name comes from addressLocalized (matches the drawer's search).
    return byOrderType.filter(
      (f) =>
        (f.address || f.name || "").toLowerCase().includes(q) ||
        (f.addressLocalized || "").toLowerCase().includes(q)
    );
  }, [activeFranchises, activeTab, searchQuery, isDeliveryTab]);

  const selectedFranchise = filteredFranchises.find(
    (f) => f.id === selectedFranchiseId
  );

  // activeDeliveryTypeId is set for any delivery-type tab (car pickup,
  // beach delivery, toktok pickup, ...) — reaching handleProceed at all
  // means it's a non-map one, since map-based tabs render the map instead.
  const proceedOrderType =
    activeDeliveryTypeId !== undefined
      ? OrderTypesEnum.DELIVERYV2
      : (activeTab as OrderTypesEnum);

  // Proceeding and confirming an address both land on Home, so fetch its code
  // and data while the guest is still choosing — exactly the link Proceed will
  // open once a branch is picked. Requested only on tap, the page data alone took
  // over a second whenever the CDN edge didn't have it (prepits/project#4344).
  useEffect(() => {
    if (!hasWelcomeDrawerV3) {
      return;
    }
    const link = getPageLink(
      router,
      PagesEnum.Home,
      selectedFranchise
        ? {
            franchiseSlug: selectedFranchise.slug,
            orderType: proceedOrderType,
          }
        : {}
    );
    router.prefetch?.(link.href, link.as)?.catch(() => undefined);
  }, [hasWelcomeDrawerV3, selectedFranchise?.id, proceedOrderType]); // eslint-disable-line

  const handleProceed = () => {
    if (!selectedFranchise) {
      return;
    }

    const orderType = proceedOrderType;

    const matchedDeliveryConfig =
      activeDeliveryTypeId !== undefined
        ? (selectedFranchise.franchiseConfig?.deliveryV2 ?? []).find(
            (c) => c.deliveryType.id === activeDeliveryTypeId
          )
        : undefined;

    // Switching to a different branch with items already in the cart needs the
    // "cart will be cleared" confirmation first — same guard as the welcome
    // drawer and the delivery selection drawer. The modal handles the reset,
    // cookie and navigation itself.
    if (
      cartItemsNumber > 0 &&
      reduxSelectedFranchise &&
      reduxSelectedFranchise.id !== selectedFranchise.id
    ) {
      handleModal(true, "SwitchFranchise/SwitchFranchise", {
        componentProps: {
          franchise: selectedFranchise,
          returnPage: PagesEnum.Home,
          orderType,
          // Commit the picked delivery type through the switch — otherwise the
          // new branch lands with a stale config (prepits/project#3913).
          deliveryConfig: matchedDeliveryConfig ?? null,
        },
      });

      return;
    }

    const chooseDate = new Date();
    chooseDate.setHours(chooseDate.getHours() + 2);
    Cookies.set(
      COOKIE_ENUM.LAST_SELECTED_FRANCHISE,
      // slug can be null on the slug-less main franchise; fall back to id.
      String(selectedFranchise.slug ?? selectedFranchise.id),
      { expires: chooseDate }
    );

    addCustomerAnalytics(
      CUSTOMER_ANALYTICS_EVENT.CONFIRM_BRANCH_AND_ORDER_TYPE,
      {
        franchiseId: selectedFranchise.id,
        franchiseSlug: selectedFranchise.slug,
        orderType,
      },
      selectedFranchise.id,
      selectedFranchise.slug
    );

    dispatch(setSelectedFranchise(selectedFranchise));
    dispatch(setQueryParam(orderType));
    dispatch(setCurrentDeliveryConfig(matchedDeliveryConfig ?? null));
    dispatch(setClickDetected(true));

    const link = getPageLink(router, PagesEnum.Home, {
      franchiseSlug: selectedFranchise.slug,
      orderType,
    });
    router.push(link.href, link.as);
  };

  const handleDetectLocation = (tries = 0) => {
    // The isDetecting guard only applies to a fresh user click, not the internal
    // retries below (which run while isDetecting is already true).
    if (tries === 0) {
      if (isDetecting) {
        return;
      }
      addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.DETECT_LOCATION_ON_MAP);
      // The V3 branch-list FAB replaces V2's <DetectLocation>, which emitted
      // DETECT_CLOSEST_BRANCH on click. Emit it here too so the metric stays
      // continuous across the V2 -> V3 switch instead of dropping to zero.
      addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.DETECT_CLOSEST_BRANCH);
      setIsDetecting(true);
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const location = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        dispatch(setLocation(location));
        dispatch(sortActiveFranchisesByDistance(location));
        setIsDetecting(false);
      },
      (err) => {
        // A transient POSITION_UNAVAILABLE/TIMEOUT on the first high-accuracy
        // fix is common (especially on desktop), which surfaced as
        // "something went wrong" with no distances. Retry a couple of times
        // before erroring — same as useDetectCurrentLocation. Permission-denied
        // (code 1) is terminal, so don't retry it.
        if (err.code !== 1 && tries < 2) {
          handleDetectLocation(tries + 1);

          return;
        }

        setIsDetecting(false);
        const errorMessage =
          err.code === 1
            ? t("delivery:enable-location")
            : err.code === 3
            ? t("delivery:connection-timeout")
            : t("common:something-went-wrong");
        dispatch(
          setAnimatedGlobalBannerState({
            isOpen: true,
            text: errorMessage,
            warning: false,
            info: true,
          })
        );
      },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    );
  };

  // ── Delivery map render helper ────────────────────────
  const renderMap = () => {
    if (enableInternalMaps) {
      return (
        <MapLibreMapComponent
          center={center}
          zoom={zoom}
          height="100%"
          onChange={onMapLibreChange}
          onLoaded={(data) => {
            if (data) {
              setMapLibreLoaded(true);
            }
          }}
        />
      );
    } else {
      return (
        <GoogleMapReact
          yesIWantToUseGoogleMapApiInternals={true}
          options={createMapOptions}
          bootstrapURLKeys={{
            key: envConfig.googleMapsApiKey!,
            libraries: ["places", "geometry"],
          }}
          center={center}
          defaultCenter={egyptDefaultProps.center}
          defaultZoom={egyptDefaultProps.zoom}
          onChange={onChange}
          onGoogleApiLoaded={(data) => {
            if (data?.maps) {
              setGoogleApiLoaded(true);
            }
          }}
          zoom={zoom}
        />
      );
    }
  };

  const activeTabLabel =
    TAB_CONFIG.find((tab) => tab.type === activeTab)?.label ?? "";

  // ── Render ────────────────────────────────────────────
  return (
    <>
      {/* ── V3 HEADER + TAB BAR ── */}
      {hasWelcomeDrawerV3 && (
        <>
          <div className={v3Styles.header} data-testid="map-screen-v3">
            <button
              className={v3Styles.back_btn}
              onClick={v3HandleBack}
              data-testid="v3-location-back"
            >
              <BackArrowIcon />
              <span className={v3Styles.back_btn_label}>
                {t("common:back")}
              </span>
            </button>
            <span className={v3Styles.header_title}>{activeTabLabel}</span>
          </div>
          {/* The address form is a step *within* an already-chosen order type,
              so the order-type tabs don't belong there — switching type
              mid-form would strand the details being typed. The header (Back)
              still renders so the customer can leave the form. */}
          {!detailsSection && (
            <div className={v3Styles.tab_bar_wrapper}>
              <div className={v3Styles.tab_bar}>
                <div className={v3Styles.tab_bar_scroll}>
                  {TAB_CONFIG.map(({ type, label, testId }) => (
                    <button
                      key={type}
                      ref={(el) => {
                        tabButtonRefs.current[type] = el;
                      }}
                      className={cn(
                        v3Styles.tab,
                        activeTab === type && v3Styles.tab_active
                      )}
                      onClick={() => handleTabChange(type)}
                      data-testid={testId}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── DELIVERY MAP (V2 existing render, reused for V3 delivery tab) ── */}
      {/* V3: show when delivery tab is active (currentDeliveryConfig set only after confirm). */}
      {/* V2: show when currentDeliveryConfig is set (pre-selected franchise flow). */}
      {(hasWelcomeDrawerV3 ? isDeliveryTab : !!currentDeliveryConfig) && (
        <div
          className={cn(
            styles.map_main_div,
            // V3 offsets the pane below its own header + tab bar and sizes it
            // in dvh so a mobile URL bar can't push the footer CTAs off screen.
            // The tab bar isn't rendered during the address-details step.
            hasWelcomeDrawerV3 &&
              (detailsSection
                ? styles.map_main_div_v3_no_tabs
                : styles.map_main_div_v3)
          )}
        >
          {/* V2 header — hidden in V3 mode (V3 has its own header above) */}
          {!hasWelcomeDrawerV3 && (
            <div className={styles.new_address_title}>
              <button
                role="BackButton"
                data-testid={"back-button"}
                color="inherit"
                aria-label="menu"
                onClick={v2HandleBack}
                className={cn(styles.back_button, "back_button")}
              >
                <BackArrowIcon />
                <div className={styles.back_text}>{headerBackTranslation}</div>
              </button>
              <span className={styles.page_title}>
                {detailsSection
                  ? newAddressTranslation
                  : addLocationTranslation}
              </span>
            </div>
          )}
          {/* MAP */}
          <div
            className={cn(
              detailsSection ? styles.map_div_details : styles.map_div
            )}
          >
            <div
              className={styles.overlay}
              onClick={() => {
                setDetailsSection(false);
              }}
            ></div>
            {isMapLoaded && (
              <div className={styles.location_center}>
                <PinDeliveryIcon />
              </div>
            )}
            {isMapLoaded && !detailsSection && (
              <div
                className={cn(
                  styles.map_detect_location_button_container,
                  hasClickedDetectLocation &&
                    styles.map_detect_location_button_container_closed
                )}
                onClick={() => {
                  if (hasWelcomeDrawerV3) {
                    addCustomerAnalytics(
                      CUSTOMER_ANALYTICS_EVENT.DETECT_LOCATION_ON_MAP
                    );
                  }
                  setHasClickedDetectLocation(true);
                  getCurrentPosition();
                }}
                data-testid="map-detect-location-button"
              >
                <MapDetectLocationIcon size={24} />
                <div
                  className={cn(
                    styles.map_detect_location_text_wrapper,
                    hasClickedDetectLocation
                      ? styles.map_detect_location_text_wrapper_closed
                      : styles.map_detect_location_text_wrapper_open
                  )}
                >
                  <p className={styles.map_detect_location_text}>
                    {t("delivery:detect-your-location")}
                  </p>
                </div>
              </div>
            )}
            {isMapLoaded && !detailsSection && (
              <div className={styles.search_input_overlay}>
                <div
                  ref={(el) => setSearchInputContainer(el)}
                  className={styles.search_input_container}
                />
                {zoom <= 8 && enableNewDeliveryExperience && (
                  <div
                    className={styles.detect_location_text}
                    data-testid="find-your-delivery-address-text"
                  >
                    {t("delivery:find-your-delivery-address")}
                  </div>
                )}
              </div>
            )}
            {center && renderMap()}
          </div>
          {/* SEARCH BOX */}
          {isMapLoaded && center && (
            <SearchBoxV2
              setCenter={setCenter}
              setZoom={setZoom}
              detailsSection={detailsSection}
              setDetailsSection={setDetailsSection}
              center={{ lat: center.lat, lng: center.lng }}
              setError={setError}
              zoom={zoom}
              searchInputContainer={searchInputContainer}
              onSkip={hasWelcomeDrawerV3 ? v3HandleSkip : v2HandleSkip}
              skipTranslation={skipTranslation}
              hasNoSkipBtn={franchiseConfig?.hasNoSkipBtn}
              onSaveAddressSuccess={
                hasWelcomeDrawerV3 ? () => setDetailsSection(false) : undefined
              }
              activeDeliveryTypeId={
                hasWelcomeDrawerV3 ? activeDeliveryTypeId : undefined
              }
              activeDeliveryConfig={
                hasWelcomeDrawerV3 ? activeDeliveryConfig : undefined
              }
            />
          )}
        </div>
      )}

      {/* ── V3 BRANCH LIST (non-delivery tabs) ── */}
      {hasWelcomeDrawerV3 && !isDeliveryTab && (
        <div className={v3Styles.content}>
          <MapScreenV3BranchList
            activeTabLabel={activeTabLabel}
            activeOrderIconKey={activeOrderIconKey}
            franchises={filteredFranchises as any}
            selectedFranchiseId={selectedFranchiseId}
            onSelectFranchise={(id) => setSelectedFranchiseId(id)}
            searchQuery={searchQuery}
            onSearchChange={handleBranchSearchChange}
            isDetecting={isDetecting}
            onDetect={handleDetectLocation}
            canDetectLocation={Boolean(detectNearestBranchLocation)}
            onSkip={v3HandleSkip}
            onProceed={handleProceed}
            selectedFranchise={selectedFranchise}
          />
        </div>
      )}
    </>
  );
};
